"use client";

import { useState } from "react";
import { X, Send, Smile, PlusCircle, Image, Link2, CheckCircle, Sparkles, Eye, MessageSquare, Star } from "lucide-react";
import "../../styles/animations.css";

interface EditReplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialText: string;
  onSave: (text: string) => void;
  productName?: string;
}

export function EditReplyModal({ isOpen, onClose, initialText, onSave, productName }: EditReplyModalProps) {
  const [text, setText] = useState(initialText);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleSave = () => {
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      onSave(text);
      setIsSaving(false);
      onClose();
    }, 800);
  };

  const generateResponse = () => {
    setIsGenerating(true);
    // Simulate API call to generate response
    setTimeout(() => {
      setText("Thank you for your wonderful review! We're thrilled to hear that you're enjoying our product. Our team works hard to ensure quality, and feedback like yours makes it all worthwhile. We appreciate your support and hope to continue exceeding your expectations!");
      setIsGenerating(false);
    }, 1500);
  };

  const addDiscount = () => {
    setText(text + "\n\nAs a token of our appreciation, we'd like to offer you a 15% discount on your next purchase. Use code: THANKYOU15");
  };

  const addTemplate = (template: string) => {
    setText(template);
  };

  // Response templates
  const templates = [
    {
      name: "Thank You",
      text: "Thank you for your wonderful review! We're thrilled to hear that you're enjoying our product. Your support means a lot to us!"
    },
    {
      name: "Appreciation",
      text: "We really appreciate your positive feedback! It's customers like you that inspire us to continue providing the best products and service possible."
    },
    {
      name: "Discount Offer",
      text: "Thank you for your amazing review! We're so happy you're enjoying our product. As a token of our appreciation, please enjoy 15% off your next purchase with code: THANKYOU15"
    },
    {
      name: "Feedback Response",
      text: "Thank you for sharing your feedback with us. We value your input and are constantly working to improve our products and services. Your comments have been forwarded to our team."
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto animate-fadeInScale">
        <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-medium">Edit Response</h3>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600 transform hover:rotate-90"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          {productName && (
            <div className="mb-5 animate-fadeIn">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700">
                {productName}
              </div>
            </div>
          )}
          
          <div className="mb-4 animate-fadeIn delay-100">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Response
              </label>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowPreview(false)}
                  className={`text-xs px-3 py-1 rounded-md transition-all duration-200 ${!showPreview ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                >
                  Edit
                </button>
                <button 
                  onClick={() => setShowPreview(true)}
                  className={`text-xs px-3 py-1 rounded-md transition-all duration-200 ${showPreview ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
                >
                  Preview
                </button>
              </div>
            </div>
            
            {!showPreview ? (
              <>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full p-4 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-sm min-h-[200px] transition-all duration-300 animate-fadeIn"
                  placeholder="Write your response here..."
                />
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-300 transform hover:scale-110 hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600">
                      <Smile className="h-5 w-5 transition-all duration-300 hover:rotate-12" />
                    </button>
                    <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-300 transform hover:scale-110 hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600">
                      <Image className="h-5 w-5 transition-all duration-300 hover:rotate-12" />
                    </button>
                    <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-300 transform hover:scale-110 hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600">
                      <Link2 className="h-5 w-5 transition-all duration-300 hover:rotate-12" />
                    </button>
                  </div>
                  <button 
                    onClick={generateResponse}
                    disabled={isGenerating}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-1 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500 ${isGenerating ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isGenerating ? (
                      <>
                        <div className="h-3.5 w-3.5 border-2 border-gray-800 dark:border-gray-200 border-t-transparent rounded-full animate-spin"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>Generate</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="w-full p-4 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-sm min-h-[200px] animate-fadeIn">
                {text.split('\n').map((line, i) => (
                  <p key={i} className={i > 0 ? 'mt-2' : ''}>{line || <br />}</p>
                ))}
              </div>
            )}
          </div>
          
          <div className="mb-6 animate-fadeIn delay-200">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Templates
              </label>
              <button className="text-xs text-gray-900 dark:text-gray-100 hover:underline">
                Manage Templates
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((template, index) => (
                <button
                  key={index}
                  onClick={() => addTemplate(template.text)}
                  className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 text-sm hover:ring-1 hover:ring-gray-400 dark:hover:ring-gray-500 transform hover:-translate-y-1 hover:shadow-md animate-fadeInScale stagger-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="font-medium mb-1 flex items-center gap-2">
                    <MessageSquare className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                    {template.name}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-xs line-clamp-2">{template.text}</div>
                </button>
              ))}
              <button className="p-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 flex flex-col items-center justify-center text-gray-600 dark:text-gray-400 transform hover:-translate-y-1 hover:shadow-md hover:ring-1 hover:ring-gray-400 dark:hover:ring-gray-500 animate-fadeInScale" style={{ animationDelay: '0.4s' }}>
                <PlusCircle className="h-5 w-5 mb-1 transition-all duration-300 hover:rotate-90" />
                <span className="text-sm">Add Template</span>
              </button>
            </div>
          </div>
          
          <div className="flex justify-between animate-fadeIn delay-300">
            <div className="flex gap-3">
              <button 
                onClick={addDiscount}
                className="px-4 py-2.5 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm flex items-center gap-2 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500"
              >
                <PlusCircle className="h-4 w-4 transition-all duration-300 hover:rotate-90" />
                Add Discount
              </button>
              <button 
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2.5 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm flex items-center gap-2 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500"
              >
                <Eye className="h-4 w-4 transition-all duration-300 hover:scale-110" />
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={onClose}
                className="px-4 py-2.5 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={isSaving}
                className={`px-4 py-2.5 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300 shadow-sm flex items-center gap-2 ring-1 ring-gray-300 dark:ring-gray-600 hover:ring-2 hover:ring-gray-400 dark:hover:ring-gray-500 ${isSaving ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSaving ? (
                  <>
                    <div className="h-4 w-4 border-2 border-gray-800 dark:border-gray-200 border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 transition-all duration-300 group-hover:scale-110" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}